
import React, { useState } from 'react';
import { useApp } from '../store';
import { Shield, User as UserIcon, Lock, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';

const Login = () => {
  const { login } = useApp();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleLogin(username, password);
  };

  const handleLogin = async (u: string, p: string) => {
    setLoading(true);
    setError(false);
    const success = await login(u, p);
    if (!success) {
      setError(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Dynamic Background Blurs */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[140px] -translate-y-1/2 translate-x-1/3 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[140px] translate-y-1/3 -translate-x-1/4"></div>

      <div className="w-full max-w-lg relative z-10 space-y-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.4)] p-6 md:p-8 border border-white/40">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-xl shadow-indigo-500/30 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
              <Shield className="w-7 h-7" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Nice Digital</h1>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className="text-slate-400 font-bold text-[9px] uppercase tracking-[0.4em] opacity-80">Operational Control Portal</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="block text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1.5 ml-2">Enter your User Name</label>
                <div className="relative group">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" />
                  <input 
                    required
                    type="text" 
                    placeholder="e.g. Chand"
                    className="w-full pl-12 pr-6 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-[1.25rem] text-sm font-bold focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:outline-none focus:bg-white transition-all text-slate-900 placeholder:text-slate-300 placeholder:font-medium shadow-inner"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1.5 ml-2">Enter password</label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors" />
                  <input 
                    required
                    type="password" 
                    placeholder="••••••••••••"
                    className="w-full pl-12 pr-6 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-[1.25rem] text-sm font-bold focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:outline-none focus:bg-white transition-all text-slate-900 placeholder:text-slate-300 placeholder:font-medium shadow-inner"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 rounded-[1rem] flex items-center gap-3 border border-red-100 animate-in fade-in slide-in-from-top-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></div>
                <p className="text-[9px] font-black text-red-600 uppercase tracking-widest leading-none">Auth Failed. Verify User Name & Access Key.</p>
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-[0.25em] rounded-[1.25rem] shadow-xl hover:shadow-indigo-500/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Log In <ArrowRight className="w-3.5 h-3.5" /></>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-4 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-700">
             <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                <span className="text-[8px] font-black uppercase tracking-widest text-slate-900">Encrypted</span>
             </div>
             <div className="w-0.5 h-0.5 bg-slate-300 rounded-full"></div>
             <div className="flex items-center gap-1.5">
                <Shield className="w-3 h-3 text-indigo-600" />
                <span className="text-[8px] font-black uppercase tracking-widest text-slate-900">Protected</span>
             </div>
          </div>
        </div>

        <div className="text-center space-y-1">
          <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.5em] leading-relaxed">
            Nice Digital Agency &bull; Workspace
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
